import Footer from "@/components/Footer";
import Header from "@/components/Header";
import React, { FormEvent, useState } from "react";
import "@/components/ui/file-input.css";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";

const Translator = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasToken, setHasToken] = useState(false);
  const [useTemplate, setUseTemplate] = useState(false);
  const interval = React.useRef<number>(1000);

  React.useEffect(() => {
    const token = document.cookie.match(/(?:^|;\s*)token=([^;]+)/)?.[1];
    setHasToken(!!token);
  }, []);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileNameElement = document.getElementById('fileName');
    if (fileNameElement) {
      fileNameElement.textContent = event.target.files?.[0]?.name || '未选择文件';
    }
  };
  const currentProgress = React.useRef<number>(0);
  const timerRef = React.useRef<NodeJS.Timeout | null>(null);
  const currentJobId = React.useRef<string | null>(null);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    const formData = new FormData(e.currentTarget);

    if (!useTemplate) {
      formData.append('template', 'no');
    }

    const progressCard = document.getElementById('progressCard');
    const statusElement = document.getElementById('status');
    const bar = document.getElementById('bar');
    const pct = document.getElementById('pct');

    // Get access code
    const accessCode = formData.get('accessCode') as string;
    const token = document.cookie.match(/(?:^|;\s*)token=([^;]+)/)?.[1];

    if (!token && !accessCode) {
      setError('请输入访问口令');
      setIsLoading(false);
      return;
    }

    try {
      // Show progress card
      currentProgress.current = 0;
      progressCard?.classList.remove('hidden');
      statusElement!.textContent = '正在上传文件...';

      const headers: HeadersInit = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      } else {
        headers['x-access-code'] = accessCode;
      }

      const response = await fetch('/api/translateAsync', {
        method: 'POST',
        headers: headers,
        body: formData,
      });
      if (!response.ok) {
        throw new Error(`Translation failed: ${response.statusText}`);
      }
      
      const result = await response.json();
      statusElement!.textContent = '任务已创建，开始处理…';
      setProgress(currentProgress.current);
      currentJobId.current = result.jobId;
      clearTimeout(timerRef.current!);
      timerRef.current = setTimeout(check, interval.current);
      
      // You can handle the response here (e.g., show download links)
      const linksDiv = document.getElementById('links');
      if (linksDiv && result.downloadUrl) {
        linksDiv.innerHTML = `<a href="${result.downloadUrl}" class="text-primary hover:underline">下载翻译结果</a>`;
      }

    } 
    catch(err) {
        if (err.message?.includes('Unauthorized') || err.message?.includes('403')) {
          setError('访问被拒绝：请检查访问口令或登录状态');
        } else {
            setError(err instanceof Error ? err.message : '翻译过程中出现错误');
        }
        statusElement!.textContent = '发生错误，请重试';
    } 
    finally {
      setIsLoading(false);
    }
  };

  async function check() {
    if (!currentJobId.current) return;
    const statusElement = document.getElementById('status');
    try {
      const token = document.cookie.match(/(?:^|;\s*)token=([^;]+)/)?.[1];
      const pass = (document.querySelector('input[name="accessCode"]') as HTMLInputElement)?.value || '';
      
      const headers: HeadersInit = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      } else {
        headers['x-access-code'] = pass;
      }

      const r = await fetch('/api/translation/' + currentJobId.current, {
        headers: headers
      });
      const s = await r.json();

      // 根据返回的 progress 更新进度条（0~100，带小数）
      if (s.status === 'Queued' || s.status === 'InProgress') {
        currentProgress.current += 10;
        const nextInerval = interval.current + 500;
        interval.current = Math.min(nextInerval, 4000); // 每次增加 0.5 秒查询间隔
        clearTimeout(timerRef.current!);
        timerRef.current = setTimeout(check, interval.current);
        if (currentProgress.current > 98) {
          currentProgress.current = 98; // 保持在 98% 以下，等待最终完成时再跳到 100%
        }
        setProgress(currentProgress.current);
      } else if (s.status === 'Failed') {
        clearTimeout(timerRef.current!);
        statusElement.textContent = '翻译失败：' + (s.errorMessage || '未知错误');
      } 
      else if (s.status === 'Completed') {
        clearTimeout(timerRef.current!);
        setProgress(100);
        statusElement.textContent = '翻译完成！下载链接如下（2 小时内有效）：';
        renderLinks(s);
        // refreshButton.style.display = 'inline-block';
        // hintText.textContent = '提示：如需长期保存，请尽快下载；链接过期后可点击“刷新下载链接”。';
      } else if (s.status === 'Unpaid') {
        statusElement.textContent = '积分不足：请前往后台充值后再试。';
      } else if (s.status === 'NotSupported') {
        statusElement.textContent = '该文件类型暂不支持。';
      } else {
        // 其它状态（Analyzing/Processing/Waiting/…）仅用进度条展示即可
      }
    } catch (err) {
      statusElement.textContent = '查询出错：' + err.message;
    }
  }

  const setProgress = (progress: number) => {
    // Ensure progress is between 0 and 100
    const value = Math.max(0, Math.min(100, Number(progress) || 0));
    
    // Get DOM elements
    const bar = document.getElementById('bar');
    const pct = document.getElementById('pct');
    const statusElement = document.getElementById('status');
    
    if (bar && pct) {
      // Update progress bar width with smooth transition
      bar.style.width = `${value}%`;
      
      // Update percentage text (remove .0 if whole number)
      pct.textContent = value.toFixed(1).replace(/\.0$/, '') + '%';
      
      // Update status message based on progress
      if (statusElement) {
        if (value === 0) {
          statusElement.textContent = '准备开始...';
        } else if (value < 100) {
          statusElement.textContent = '正在处理中...';
        }
      }
    }
  }

  const renderLinks = (result: { url?: string; sxsUrl?: string }) => {
    const linksDiv = document.getElementById('links');
    if (!linksDiv) return;

    linksDiv.innerHTML = '';
    
    if (result.url) {
      const translatedLink = document.createElement('div');
      translatedLink.className = 'mb-2';
      translatedLink.innerHTML = `
        <a 
          href="${result.url}" 
          target="_blank" 
          rel="noopener noreferrer" 
          class="inline-flex items-center space-x-2 text-primary hover:underline"
        >
          <span>📄</span>
          <span>译文下载</span>
        </a>
      `;
      linksDiv.appendChild(translatedLink);
    }

    if (result.sxsUrl) {
      const bilingualLink = document.createElement('div');
      bilingualLink.className = 'mb-2';
      bilingualLink.innerHTML = `
        <a 
          href="${result.sxsUrl}" 
          target="_blank" 
          rel="noopener noreferrer" 
          class="inline-flex items-center space-x-2 text-primary hover:underline"
        >
          <span>📑</span>
          <span>双语对照文件下载</span>
        </a>
      `;
      linksDiv.appendChild(bilingualLink);
    }

    // Show the refresh button
    const refreshButton = document.getElementById('refresh');
    if (refreshButton) {
      refreshButton.classList.remove('hidden');
    }

    // Show hint text
    const hintElement = document.getElementById('hint');
    if (hintElement) {
      hintElement.textContent = '提示：如需长期保存，请尽快下载；链接过期后可点击"刷新下载链接"。';
    }
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-8 mt-16 max-w-3xl">
        <h1 className="text-3xl font-bold text-center mb-4">Kuaifan 文档翻译</h1>
        <p className="text-muted-foreground text-center mb-8">把文件翻译为 <b>简体中文</b>。下载链接约 2 小时有效。</p>
        
        <div className="bg-card rounded-lg shadow-lg p-6 mb-8">
          <form id="form" encType="multipart/form-data" className="space-y-6" onSubmit={handleSubmit}>
            <div className="space-y-2">
              <label className="block text-sm font-medium mb-1">选择PDF文件</label>
              <div className="flex items-center gap-2">
                <input 
                  type="file" 
                  name="file" 
                  id="file"
                  accept=".pdf" 
                  required
                  className="hidden"
                  onChange={handleFileChange}
                />
                <button
                  type="button"
                  onClick={() => document.getElementById('file')?.click()}
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
                >
                  选择文件
                </button>
                <span className="text-sm text-muted-foreground" id="fileName">未选择文件</span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="block text-sm font-medium mb-1">源语言</label>
                <select name="fromLang" defaultValue="Auto" required className="w-full p-2 border rounded-md bg-background">
                  <option value="Auto">自动</option>
                </select>
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium mb-1">目标语言</label>
                <select name="toLang" defaultValue="Simplified Chinese" required className="w-full p-2 border rounded-md bg-background">
                  <option value="Simplified Chinese">简体中文</option>
                  <option value="English">英文</option>
                </select>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Switch 
                id="use-template" 
                checked={useTemplate}
                onCheckedChange={setUseTemplate}
              />
              <Label htmlFor="use-template">使用模板</Label>
            </div>

            {useTemplate && (
              <div className="space-y-2">
                <label className="block text-sm font-medium mb-1">选择模版</label>
                <select name="template" defaultValue="driver_license_china" className="w-full p-2 border rounded-md bg-background">
                  <option value="driver_license_china">中国驾照</option>
                </select>
              </div>
            )}

            {!hasToken && (
            <div className="space-y-2">
              <label className="block text-sm font-medium mb-1">访问口令</label>
              <input 
                type="password" 
                name="accessCode" 
                placeholder="请输入访问口令" 
                className="w-full p-2 border rounded-md bg-background"
              />
            </div>
            )}

            <button 
              type="submit" 
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90 py-2 px-4 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isLoading}
            >
              {isLoading ? '处理中...' : '开始翻译'}
            </button>
            <p className="text-xs text-blue-500 text-center mt-2">试用版，只翻译前十页</p>
            {error && (
              <p className="text-destructive text-sm text-center">{error}</p>
            )}
            <p id="status" className="text-muted-foreground text-center"></p>
          </form>
        </div>

        <div className="bg-card rounded-lg shadow-lg p-6 mb-8 hidden" id="progressCard">
          <h3 className="text-xl font-semibold mb-4">处理进度</h3>
          <div className="relative pt-1">
            <div className="overflow-hidden h-2 mb-4 text-xs flex rounded bg-muted">
              <div className="w-0 transition-all duration-300 ease-out bg-primary" id="bar"></div>
            </div>
            <div className="text-center text-sm font-semibold"><span id="pct">0%</span></div>
          </div>
        </div>

        <div className="bg-card rounded-lg shadow-lg p-6">
          <h3 className="text-xl font-semibold mb-4">结果</h3>
          <div className="space-y-2" id="links"></div>
          <button 
            id="refresh" 
            className="mt-4 bg-secondary text-secondary-foreground hover:bg-secondary/90 py-2 px-4 rounded-md transition-colors hidden"
          >
            刷新下载链接
          </button>
          <p className="text-muted-foreground mt-2" id="hint"></p>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Translator;
